import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { useHistory } from 'react-router-dom'
import { LucideIcon, LucideIconName } from 'utils/lucideIcon'
import './kebabMenu.scss'

export interface KebabMenuOption {
    label: string
    link?: string
    icon?: LucideIconName
    onClick?: () => void
}

interface KebabMenuProps {
    options: KebabMenuOption[]
    className?: string
    listAnchor?: 'left' | 'right'
    disabled?: boolean
    triggerIcon?: LucideIconName
}

export const KebabMenu = ({
    options,
    className = '',
    listAnchor = 'left',
    disabled,
    triggerIcon = 'EllipsisVertical',
}: KebabMenuProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const history = useHistory()

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    return (
        <div ref={menuRef} className={classNames('kebab-menu', className, { disabled })}>
            <div
                className="kebab-menu-trigger"
                onClick={(event) => {
                    event.stopPropagation()
                    if (!disabled) setIsOpen(!isOpen)
                }}
            >
                <LucideIcon iconName={triggerIcon} strokeWidth={2} />
            </div>
            <div className={classNames(`kebab-menu-options ${listAnchor}`, { open: isOpen })}>
                {options.map((option) => (
                    <div
                        className="kebab-menu-option"
                        key={option.label}
                        onClick={(event) => {
                            event.stopPropagation()
                            setIsOpen(false)
                            if (option.onClick) {
                                option.onClick()
                            } else if (option.link) {
                                history.push(option.link)
                            }
                        }}
                    >
                        {option.icon && <LucideIcon iconName={option.icon} className="kebab-menu-option-icon" />}
                        <span className="kebab-menu-option-label">{option.label}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}
